/**
 * MCP Server Factory
 *
 * Creates a configured MCP Server instance without connecting a transport.
 * This allows the server to be used with different transports (stdio, HTTP).
 */

import { createRequire } from 'node:module';
import { Server } from '@modelcontextprotocol/sdk/server/index.js';
import {
  CallToolRequestSchema,
  ListToolsRequestSchema,
  ListPromptsRequestSchema,
} from '@modelcontextprotocol/sdk/types.js';
import type { McpServerConfig } from './types.js';
import type { CreateTestJobRequest } from '@runhuman/shared';
import type { RerunJobArgs } from './tools/rerun-job.tool.js';
import type { WaitArgs } from './tools/wait.tool.js';
import type { GetJobArgs } from './tools/get-job.tool.js';
import type { ListTemplatesArgs } from './tools/list-templates.tool.js';
import type { ListProjectsArgs } from './tools/list-projects.tool.js';
import type { GetTestArtifactsArgs } from './tools/get-test-artifacts.tool.js';
import type { ListSchedulesArgs } from './tools/list-schedules.tool.js';
import type { CreateScheduleArgs } from './tools/create-schedule.tool.js';
import type { UpdateScheduleArgs } from './tools/update-schedule.tool.js';
import type { DeleteScheduleArgs } from './tools/delete-schedule.tool.js';
import type { CreateTemplateArgs } from './tools/create-template.tool.js';
import type { UpdateTemplateArgs } from './tools/update-template.tool.js';
import type { DeleteTemplateArgs } from './tools/delete-template.tool.js';
import type { ListOrganizationMembersArgs } from './tools/list-organization-members.tool.js';
import type { InviteOrganizationMemberArgs } from './tools/invite-organization-member.tool.js';
import type { ListTesterNotesArgs } from './tools/list-tester-notes.tool.js';
import type { CreateTesterNoteArgs } from './tools/create-tester-note.tool.js';
import type { UpdateTesterNoteArgs } from './tools/update-tester-note.tool.js';
import type { DeleteTesterNoteArgs } from './tools/delete-tester-note.tool.js';
import type { RenderBakedDemoVideoArgs } from './tools/render-baked-demo-video.tool.js';
import {
  createJobToolDefinition,
  handleCreateJob,
  rerunJobToolDefinition,
  handleRerunJob,
  waitToolDefinition,
  handleWait,
  runTemplateToolDefinition,
  handleRunTemplate,
  getJobToolDefinition,
  handleGetJob,
  listTemplatesToolDefinition,
  handleListTemplates,
  listOrganizationsToolDefinition,
  handleListOrganizations,
  listProjectsToolDefinition,
  handleListProjects,
  getTestArtifactsToolDefinition,
  handleGetTestArtifacts,
  listSchedulesToolDefinition,
  handleListSchedules,
  createScheduleToolDefinition,
  handleCreateSchedule,
  updateScheduleToolDefinition,
  handleUpdateSchedule,
  deleteScheduleToolDefinition,
  handleDeleteSchedule,
  createTemplateToolDefinition,
  handleCreateTemplate,
  updateTemplateToolDefinition,
  handleUpdateTemplate,
  deleteTemplateToolDefinition,
  handleDeleteTemplate,
  listOrganizationMembersToolDefinition,
  handleListOrganizationMembers,
  inviteOrganizationMemberToolDefinition,
  handleInviteOrganizationMember,
  listTesterNotesToolDefinition,
  handleListTesterNotes,
  createTesterNoteToolDefinition,
  handleCreateTesterNote,
  updateTesterNoteToolDefinition,
  handleUpdateTesterNote,
  deleteTesterNoteToolDefinition,
  handleDeleteTesterNote,
  renderBakedDemoVideoToolDefinition,
  handleRenderBakedDemoVideo,
} from './tools/index.js';
import { createJobClient } from './clients/index.js';

// Report the real package version to MCP clients instead of a hardcoded string
// that silently drifts on every release. `../package.json` resolves relative to
// the built `dist/` file at runtime; a runtime require sidesteps the `rootDir`
// constraint that a compile-time JSON import would violate.
const { version: SERVER_VERSION } = createRequire(import.meta.url)('../package.json') as { version: string };

export function createMcpServer(config: McpServerConfig): Server {
  const jobClient = createJobClient(config);

  const server = new Server(
    {
      name: 'runhuman-mcp-server',
      version: SERVER_VERSION,
    },
    {
      capabilities: {
        tools: {},
        prompts: {},
      },
    }
  );

  server.setRequestHandler(ListPromptsRequestSchema, async () => ({
    prompts: [
      {
        name: 'explain_runhuman',
        description: 'Get an explanation of how to use Runhuman for QA testing',
      },
    ],
  }));

  server.setRequestHandler(ListToolsRequestSchema, async () => ({
    tools: [
      listOrganizationsToolDefinition,
      listOrganizationMembersToolDefinition,
      inviteOrganizationMemberToolDefinition,
      listProjectsToolDefinition,
      createJobToolDefinition,
      rerunJobToolDefinition,
      runTemplateToolDefinition,
      waitToolDefinition,
      getJobToolDefinition,
      getTestArtifactsToolDefinition,
      listTemplatesToolDefinition,
      listSchedulesToolDefinition,
      createScheduleToolDefinition,
      updateScheduleToolDefinition,
      deleteScheduleToolDefinition,
      createTemplateToolDefinition,
      updateTemplateToolDefinition,
      deleteTemplateToolDefinition,
      listTesterNotesToolDefinition,
      createTesterNoteToolDefinition,
      updateTesterNoteToolDefinition,
      deleteTesterNoteToolDefinition,
      renderBakedDemoVideoToolDefinition,
    ],
  }));

  server.setRequestHandler(CallToolRequestSchema, async (request) => {
    const { name, arguments: args } = request.params;

    if (!args) {
      throw new Error('Missing arguments');
    }

    switch (name) {
      case 'create_job':
        return handleCreateJob(args as unknown as CreateTestJobRequest, jobClient);

      case 'rerun_job':
        return handleRerunJob(args as unknown as RerunJobArgs, jobClient);

      case 'run_template':
        return handleRunTemplate(args as unknown as CreateTestJobRequest & { template: string }, jobClient);

      case 'wait':
        return handleWait(args as unknown as WaitArgs, jobClient);

      case 'get_job':
        return handleGetJob(args as unknown as GetJobArgs, jobClient);

      case 'list_templates':
        return handleListTemplates(args as unknown as ListTemplatesArgs, jobClient);

      case 'list_organizations':
        return handleListOrganizations(jobClient);

      case 'list_organization_members':
        return handleListOrganizationMembers(args as unknown as ListOrganizationMembersArgs, jobClient);

      case 'invite_organization_member':
        return handleInviteOrganizationMember(args as unknown as InviteOrganizationMemberArgs, jobClient);

      case 'list_projects':
        return handleListProjects(args as unknown as ListProjectsArgs, jobClient);

      case 'get_test_artifacts':
        return handleGetTestArtifacts(args as unknown as GetTestArtifactsArgs, jobClient);

      case 'list_schedules':
        return handleListSchedules(args as unknown as ListSchedulesArgs, jobClient);

      case 'create_schedule':
        return handleCreateSchedule(args as unknown as CreateScheduleArgs, jobClient);

      case 'update_schedule':
        return handleUpdateSchedule(args as unknown as UpdateScheduleArgs, jobClient);

      case 'delete_schedule':
        return handleDeleteSchedule(args as unknown as DeleteScheduleArgs, jobClient);

      case 'create_template':
        return handleCreateTemplate(args as unknown as CreateTemplateArgs, jobClient);

      case 'update_template':
        return handleUpdateTemplate(args as unknown as UpdateTemplateArgs, jobClient);

      case 'delete_template':
        return handleDeleteTemplate(args as unknown as DeleteTemplateArgs, jobClient);

      case 'list_tester_notes':
        return handleListTesterNotes(args as unknown as ListTesterNotesArgs, jobClient);

      case 'create_tester_note':
        return handleCreateTesterNote(args as unknown as CreateTesterNoteArgs, jobClient);

      case 'update_tester_note':
        return handleUpdateTesterNote(args as unknown as UpdateTesterNoteArgs, jobClient);

      case 'delete_tester_note':
        return handleDeleteTesterNote(args as unknown as DeleteTesterNoteArgs, jobClient);

      case 'render_baked_demo_video':
        return handleRenderBakedDemoVideo(args as unknown as RenderBakedDemoVideoArgs, jobClient);

      default:
        throw new Error(`Unknown tool: ${name}`);
    }
  });

  return server;
}
